import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { canAccess, ROLE_BADGE } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import {
  House, GraduationCap, ChalkboardTeacher, BookOpen, CalendarBlank, ClipboardText,
  CurrencyDollar, ChartLine, UsersThree, Gear, CaretDoubleLeft, CaretDoubleRight, SignOut,
  Megaphone, ChatCircle, UserCircle, PresentationChart,
} from "@phosphor-icons/react";

const NAV = [
  { key: "dashboard", to: "/", icon: House },
  { key: "portal", to: "/portal", icon: UserCircle },
  { key: "teacher_portal", to: "/teacher-portal", icon: PresentationChart },
  { key: "students", to: "/students", icon: GraduationCap },
  { key: "teachers", to: "/teachers", icon: ChalkboardTeacher },
  { key: "courses", to: "/courses", icon: BookOpen },
  { key: "classes", to: "/classes", icon: CalendarBlank },
  { key: "attendance", to: "/attendance", icon: ClipboardText },
  { key: "payments", to: "/payments", icon: CurrencyDollar },
  { key: "messages", to: "/messages", icon: ChatCircle },
  { key: "announcements", to: "/announcements", icon: Megaphone },
  { key: "reports", to: "/reports", icon: ChartLine },
  { key: "users", to: "/users", icon: UsersThree },
  { key: "settings", to: "/settings", icon: Gear },
];

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const role = user?.role;
  const items = NAV.filter((n) => canAccess(n.key, role));
  const badge = ROLE_BADGE[role];

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "h-screen sticky top-0 border-r border-border bg-card flex flex-col",
        "transition-[width] duration-200 ease-out",
        collapsed ? "w-[72px]" : "w-[248px]",
      )}
    >
      {/* Brand */}
      <div className="h-16 flex items-center gap-3 px-4 border-b border-border">
        <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center shrink-0">
          <span className="text-primary-foreground font-bold text-lg" style={{ fontFamily: "Manrope" }}>B</span>
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <div className="font-bold text-[15px] leading-tight" style={{ fontFamily: "Manrope" }}>
              {t("brand.name")}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {t("brand.tagline")}
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-2">
        {!collapsed && (
          <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {t("nav.workspace")}
          </div>
        )}
        <ul className="space-y-0.5">
          {items.map(({ key, to, icon: Icon }) => (
            <li key={key}>
              <NavLink
                to={to}
                end={to === "/"}
                data-testid={`nav-${key}`}
                className={({ isActive }) =>
                  cn(
                    "group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium",
                    "transition-colors duration-150",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                    collapsed && "justify-center px-0",
                  )
                }
              >
                <Icon size={18} weight="duotone" />
                {!collapsed && <span>{t(`nav.${key}`)}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User & collapse */}
      <div className="border-t border-border p-3 space-y-2">
        {!collapsed && user && (
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center overflow-hidden shrink-0">
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-sm font-semibold">{user.name?.charAt(0)}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user.name}</div>
              {badge && (
                <span className={cn("inline-block text-[10px] font-semibold px-2 py-0.5 rounded", badge.color)}>
                  {t(`roles.${user.role}`, { defaultValue: badge.label })}
                </span>
              )}
            </div>
          </div>
        )}
        <div className={cn("flex gap-1", collapsed && "flex-col")}>
          <button
            data-testid="sidebar-collapse-btn"
            onClick={() => setCollapsed((v) => !v)}
            className="flex-1 flex items-center justify-center gap-2 px-2 py-2 rounded-md text-xs text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
          >
            {collapsed ? <CaretDoubleRight size={16} /> : <><CaretDoubleLeft size={16} /><span>{t("nav.collapse")}</span></>}
          </button>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-xs text-muted-foreground hover:bg-destructive hover:text-destructive-foreground transition-colors"
          >
            <SignOut size={16} />
            {!collapsed && <span>{t("nav.signOut")}</span>}
          </button>
        </div>
      </div>
    </aside>
  );
}
